import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Button, Snackbar, Stack } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { FieldMapping } from '../../models/configuration/xpathFieldMap/IXpathFieldMap';
import { AlertSeverity } from '../../constants/AlertTypes';
import { ValidateXPathXpression, XpathFieldIdPrefix } from '../../constants/XpathConstants';
import { DtDialog } from '../ui/dialog';
import DtForm, { DtFormHandles } from "../ui/form/DtForm";
import { FormField } from '../ui/form';

interface XPathGridItemEditDialogProps {
    open: boolean;
    closeFxtn: () => void;
    title?: string | undefined;
    item: FieldMapping | null;
    updateFxtn: (item: FieldMapping) => void;
    existingItems?: FieldMapping[];
}

const XPathGridItemEditDialog: React.FC<XPathGridItemEditDialogProps> = ({
    open, closeFxtn, title, item, updateFxtn, existingItems = []
}) => {
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<{ message: string | null, severity: 'success' | 'error' | null }>({
        message: null,
        severity: null
    });
    const formRef = useRef<DtFormHandles>(null);

    // Memoized initial values to prevent form clearing on re-renders
    const initialValues = useMemo(() => ({
        Name: item?.Name ?? "",
        EncompassFieldId: item?.EncompassFieldId ?? "",
        Xpath: item?.Xpath ?? ""
    }), [item?.Name, item?.EncompassFieldId, item?.Xpath]);

    // Form fields definition
 const fields: FormField[] = [
    {
        id: "Name",
        label: "Description",
        type: "text",
        required: true,
        grid: { xs: 12 },
        validate: (value: string) => {
            if (!value || value.trim() === "") {
                return "Description is required.";
            }
            return undefined;
        }
    },
    {
        id: "EncompassFieldId",
        label: "Output Encompass Path",
        type: "text",
        required: true,
        grid: { xs: 12 },
        validate: (value: string) => {
            if (!value || value.trim() === "") {
                return "Output Encompass Path is required.";
            }
            if (!value?.toLocaleUpperCase().startsWith(XpathFieldIdPrefix)) {
                return `Encompass Field Id must start with ${XpathFieldIdPrefix}`;
            }
            return undefined;
        }
    },
    {
        id: "Xpath",
        label: "Input XPath",
        type: "textarea",
        required: true,
        grid: { xs: 12 },
        validate: (value: string) => {
            if (!value || value.trim() === "") {
                return "Input XPath is required.";
            }
            const xapthExpression = ValidateXPathXpression(value);
            return xapthExpression?.isValid ? undefined : xapthExpression?.message;
        }
    }
];

    // Form submit handler
    const handleFormSubmit = (data: Record<string, any>) => {
        // Check for duplicate EncompassFieldId (excluding current item)
        const existingItem = existingItems.find(existingItem =>
            existingItem.EncompassFieldId?.toLocaleUpperCase() === data.EncompassFieldId?.toLocaleUpperCase() &&
            existingItem.Name !== item?.Name
        );
        if (existingItem) {
            setAlertSeverity('error');
            setAlertMessage(`Pushback fields with the given Output Encompass Path '${data.EncompassFieldId}' already exists`);
            setAlertOpen(true);
            return;
        }
        updateFxtn({ ...item, ...data } as FieldMapping);
        setAlertSeverity('success');
        setAlertMessage("Item updated successfully!");
        setAlertOpen(true);
        closeFxtn();
    };

    const handleSnackBarClose = () => setAlertOpen(false);

    const handleClear = () => {
        formRef.current?.reset();
    };

    const handleSaveAndClose = () => {
        formRef.current?.submit();
    };

    useEffect(() => {
        if (open) {
            handleClear();
            setAlertOpen(false);
            setAlertMessage('');
            setAlertSeverity('info');
            setValidationResult({ message: null, severity: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, item]);

    return (
        <DtDialog
            title={title ?? "Title"}
            open={open}
            onClose={closeFxtn}
            actions={
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button size='small' variant="text" onClick={handleClear}>Clear</Button>
                    <Button
                        size='small'
                        variant="contained"
                        onClick={handleSaveAndClose}
                    >Save & Close</Button>
                </Stack>
            }
        >
            <Box sx={{ position: "relative" }}>
                <DtForm
                    ref={formRef}
                    fields={fields}
                    onSubmit={handleFormSubmit}
                    values={initialValues}
                    showSubmit={false}
                    showReset={false}
                    labelPosition="ontop"
                />
                {validationResult.message && (
                    <Box sx={{ mt: 2 }}>
                        <MuiAlert
                            severity={validationResult.severity ?? 'info'}
                            sx={{ width: '100%', fontSize: '1.1rem', py: 1 }}
                        >
                            {validationResult.message}
                        </MuiAlert>
                    </Box>
                )}
            </Box>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={alertOpen}
                autoHideDuration={4000}
                onClose={handleSnackBarClose}
            >
                <MuiAlert
                    onClose={handleSnackBarClose}
                    severity={alertSeverity}
                    sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                    {alertMessage}
                </MuiAlert>
            </Snackbar>
        </DtDialog>
    );
};

export default XPathGridItemEditDialog;